import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Command } from 'cmdk'
import { useGists } from '../contexts/GistContext'
import { formatDate } from '../utils/formatDate'
import LanguageBadge from './LanguageBadge'
import HighlightedText from './HighlightedText'
import { useDebounce } from '../hooks/useDebounce'

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 150)
  const [filteredGists, setFilteredGists] = useState([])
  const [selectedValue, setSelectedValue] = useState('')
  const isSearching = search !== debouncedSearch
  const navigate = useNavigate()
  const { gists, search: searchGists } = useGists()
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dracula'
  })
  const [keyboardShortcut, setKeyboardShortcut] = useState(() => {
    const stored = localStorage.getItem('commandPaletteShortcut')
    if (!stored) {
      localStorage.setItem('commandPaletteShortcut', 'cmd-k')
      return 'cmd-k'
    }
    return stored
  })

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = localStorage.getItem('theme') || 'dracula'
      setTheme(newTheme)
    }

    window.addEventListener('themeChange', handleThemeChange)
    window.addEventListener('storage', handleThemeChange)

    return () => {
      window.removeEventListener('themeChange', handleThemeChange)
      window.removeEventListener('storage', handleThemeChange)
    }
  }, [])

  useEffect(() => {
    const handleShortcutChange = () => {
      const stored = localStorage.getItem('commandPaletteShortcut')
      if (stored) {
        setKeyboardShortcut(stored)
      }
    }

    window.addEventListener('commandPaletteShortcutChange', handleShortcutChange)
    window.addEventListener('storage', handleShortcutChange)

    return () => {
      window.removeEventListener('commandPaletteShortcutChange', handleShortcutChange)
      window.removeEventListener('storage', handleShortcutChange)
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (e) => {
      let shouldOpen = false

      if (keyboardShortcut === 'cmd-k') {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          shouldOpen = true
        }
      } else if (keyboardShortcut === 'slash') {
        if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey) {
          const target = e.target
          if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return
          }
          shouldOpen = true
        }
      }

      if (shouldOpen) {
        e.preventDefault()
        setOpen(true)
      }

      if (e.key === 'Escape' && open) {
        e.preventDefault()
        setOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, keyboardShortcut])

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
    if (open && listRef.current) {
      listRef.current.scrollTop = 0
    }
    if (!open) {
      setSelectedValue('')
    }
  }, [open])

  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setFilteredGists(gists.map(gist => ({ gist, terms: [] })))
      return
    }

    const results = searchGists(debouncedSearch)
    const resultGists = results
      .map((result) => ({
        gist: gists.find((g) => g.gist_id === result.gist_id),
        terms: result.terms || [],
        match: result.match,
        score: result.score
      }))
      .filter(r => r.gist)

    setFilteredGists(resultGists)
  }, [debouncedSearch, gists, searchGists])

  useEffect(() => {
    if (filteredGists.length > 0 && !selectedValue) {
      setSelectedValue(filteredGists[0].gist.gist_id)
    }
  }, [filteredGists, selectedValue])

  const handleSelect = (gistId) => {
    navigate(`/${gistId}`)
    setOpen(false)
    setSearch('')
  }

  if (!open) return null

  return (
    <div className="command-palette-overlay" onClick={() => setOpen(false)}>
      <div className="command-palette-container" onClick={(e) => e.stopPropagation()} data-theme={theme}>
        <Command
          className="command-palette"
          label="Command Menu"
          shouldFilter={false}
          value={selectedValue}
          onValueChange={setSelectedValue}
        >
          <div className="command-input-wrapper">
            <Command.Input
              ref={inputRef}
              placeholder="Search gists..."
              value={search}
              onValueChange={setSearch}
              className="command-input"
            />
            {isSearching && (
              <div className="command-spinner">
                <span className="loading loading-spinner loading-sm"></span>
              </div>
            )}
          </div>

          <Command.List ref={listRef} className="command-list">
            <Command.Empty className="command-empty">No results found.</Command.Empty>

            {filteredGists.map(({ gist, terms, match }) => {
              const matchedFields = match ? Object.values(match).flat() : []
              const hasFilenameMatch = matchedFields.includes('filenames')
              const hasContentMatch = matchedFields.includes('content')

              const matchedFilenames = hasFilenameMatch
                ? gist.files.filter(f =>
                    terms.some(term => f.filename.toLowerCase().includes(term.toLowerCase()))
                  )
                : []

              const matchedContent = hasContentMatch
                ? gist.files.find(f =>
                    terms.some(term => f.content?.toLowerCase().includes(term.toLowerCase()))
                  )
                : null

              const getContentSnippet = (content, terms) => {
                if (!content) return ''
                const lowerContent = content.toLowerCase()
                for (const term of terms) {
                  const index = lowerContent.indexOf(term.toLowerCase())
                  if (index !== -1) {
                    const start = Math.max(0, index - 30)
                    const end = Math.min(content.length, index + term.length + 30)
                    return (start > 0 ? '...' : '') +
                           content.slice(start, end) +
                           (end < content.length ? '...' : '')
                  }
                }
                return content.slice(0, 100)
              }

              return (
                <Command.Item
                  key={gist.gist_id}
                  value={gist.gist_id}
                  onSelect={() => handleSelect(gist.gist_id)}
                  className="command-item"
                >
                  <div className="flex flex-col gap-1 w-full">
                    <div className="font-medium">
                      <HighlightedText
                        text={gist.description || 'Untitled gist'}
                        terms={terms}
                      />
                    </div>

                    {matchedFilenames.length > 0 && (
                      <div className="text-xs opacity-80">
                        <span className="opacity-60">Files: </span>
                        {matchedFilenames.slice(0, 2).map((file, i) => (
                          <span key={i}>
                            {i > 0 && ', '}
                            <HighlightedText text={file.filename} terms={terms} />
                          </span>
                        ))}
                        {matchedFilenames.length > 2 && ` +${matchedFilenames.length - 2} more`}
                      </div>
                    )}

                    {matchedContent && (
                      <div className="text-xs opacity-80 font-mono">
                        <HighlightedText
                          text={getContentSnippet(matchedContent.content, terms)}
                          terms={terms}
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm">
                      <span className="opacity-70">
                        {formatDate(gist.gist_created_at)}
                      </span>
                      <>
                        {Array.from(
                          new Set(gist.files.map((f) => f.language).filter(Boolean))
                        )
                          .slice(0, 3)
                          .map((lang) => (
                            <LanguageBadge key={lang} language={lang} size="sm" />
                          ))}
                      </>
                    </div>
                  </div>
                </Command.Item>
              )
            })}
          </Command.List>
        </Command>
      </div>
    </div>
  )
}
